/**
 * Contexte de session (O-3).
 *
 * Sert UNIQUEMENT à l'affichage : masquer un bouton qu'un `viewer` ne peut pas
 * actionner, et écrire son nom dans la barre latérale. L'autorisation réelle est
 * rendue par le serveur, qui répond 403 quoi qu'affiche cette page — un contrôle
 * de droits côté navigateur n'est qu'une politesse, jamais une sécurité.
 *
 * Cacher l'action reste utile : un bouton qui échoue systématiquement fait
 * croire à une panne, alors qu'une action absente et expliquée se comprend.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { Permission, SessionInfo } from './api';

const SessionContext = createContext<SessionInfo | null>(null);

export function SessionProvider({
  session,
  children,
}: {
  session: SessionInfo;
  children: ReactNode;
}): React.ReactElement {
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionInfo | null {
  return useContext(SessionContext);
}

/**
 * `true` si l'opérateur courant détient la permission.
 *
 * Hors contexte (test isolé d'une page), on répond `false` : une page rendue
 * sans session ne doit pas afficher d'actions d'écriture.
 */
export function useCan(permission: Permission): boolean {
  const session = useContext(SessionContext);
  return session?.permissions.includes(permission) ?? false;
}

/** Phrase unique pour toutes les actions masquées — même mot partout, moins de doute. */
export const READ_ONLY_NOTICE =
  'Votre rôle ne permet pas cette action : vous disposez d’un accès en lecture seule.';
